import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useGoogleLogin} from '@react-oauth/google';
import {RecordPage} from '../../shared/components/record/RecordKit';
import {useLeaveGuard} from '../../shared/components/LeaveGuard';
import {useStreetSuggestions} from '../../shared/components/street-suggestions';
import {usePermissions} from '../../shared/permissions/PermissionContext';
import {PermissionCode} from '../../shared/permissions/permissions';
import {useUserSettings} from '../../components/UserSettingsContext';
import {ExternalCompanyDto} from '../../shared/dtos/company.dto';
import {SavaquanHttpError} from '../../shared/utils/http-error';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';
import {addCompanyIntegration, deleteCompanyIntegration, getCompanyIntegrations} from '../settings/integrations.service';
import {updateUserSettings} from '../settings/settings.service';
import {CompanyIntegrationDto} from '../settings/dtos/integration.dto';
import {CalendarIntegration} from '../settings/dtos/integration.types';
import {CloseAccountDialog} from '../settings/CloseAccountDialog';
import {capitalizeWords, formatPhoneNumber, phoneDigits} from '../client/profile/contact-entries';
import {
  dayKey, dayLabel, HoursWeek, hoursLabel, initialsOf, isLongRange, joinAddress, levelLabel, MyProfileDto, parseDay,
  recentWeeks, weekLabel, weeksBetween, weeksFromDto, withDayMinutes,
} from './account-model';
import {
  getMyCompany, getMyProfile, logoToPng, removeLogo, UnreadableLogo, updateMyCompany, updateMyHoursWorked, updateMyProfile,
  uploadLogo,
} from './account.service';
import {
  Card, Chevron, ChevronButton, Dialog, EditFoot, Field, GroupTitle, Hairline, Hint, InitialsBadge, NoticeBox, OutlinePill, SmallCaps,
} from './AccountKit';
import {PlanSummary} from './AccountBilling';
import {BusinessEmailRow, SendFromTab, useConnectReturn} from './BusinessEmail';
import './AccountPage.css';

/*
 * The Account page (SQ-652): Android's (estimatebuilder/account/
 * AccountScreen.kt and the pages it opens), opened from who is signed in at
 * the foot of the menu. Android's code is the spec for how it works.
 *
 * The web's part: it is the 750 column like every record page, with its name
 * as the page's title (nothing in the green bar, no back arrow), and NOTHING
 * opens a page of its own. Android's Edit opens a full page for Your details
 * and Business details; here Edit turns that card into its form, right where
 * it is, with Cancel and Save changes at the card's foot. One card is edited at a time: while one is open the
 * other cards' buttons are dimmed, and the open card scrolls to the top of
 * the page. Leaving with unsaved changes asks first.
 *
 * Your details, the hours, the business, its logo, the calendar and the photo
 * quality save through the same endpoints Android uses. Your plan is read
 * from the server (SQ-415) and changed on the plan page, /settings/plan,
 * where the payment method and receipts are too.
 *
 * Since SQ-743 Business details is where the company's accounts are
 * connected (Settings no longer has them): the business email (Gmail or
 * Outlook, BusinessEmail.tsx), Google Calendar, and the photo quality.
 */

type Editing = 'profile' | 'business' | null;

interface ProfileDraft {name: string; phone: string; street: string; city: string; state: string; postalCode: string}
interface BusinessDraft {name: string; phone: string; email: string; street: string; street2: string; city: string; state: string; postalCode: string}

const HOURS_EXPANDED = 2;
const STEPS = 10;
/** The web's default photo quality when nothing is saved (settings.dto.ts), as Android's PhotoQuality.DEFAULT. */
const DEFAULT_QUALITY = 0.8;

/** What the leave question names: "Your changes to <this> have not been saved." */
const UNSAVED: Record<Exclude<Editing, null>, string> = {
  profile: 'your details', business: 'the business details',
};

const qualityToStep = (quality: number) => Math.min(STEPS, Math.max(1, Math.round(quality * STEPS)));

/** The server's own words when it gave any (teamHttpErrorMessage), else the fallback. */
const errorMessage = (e: unknown, fallback: string) => {
  if (e instanceof UnreadableLogo && e.message) return e.message;
  if (!(e instanceof SavaquanHttpError) || e.statusCode === 0) return fallback;
  const details = e.details?.trim();
  if (details && details.length < 200 && !details.startsWith('{') && !details.startsWith('<')) return details;
  return e.message && !e.message.startsWith('HTTP ') ? e.message : fallback;
};

const blankToNull = (s: string) => s.trim() || null;

export const AccountPage = () => {
  const navigate = useNavigate();
  const {hasPermission, refresh: refreshMe} = usePermissions();
  const canSeePlan = hasPermission(PermissionCode.SettingsBilling);

  const [profile, setProfile] = useState<MyProfileDto>();
  const [company, setCompany] = useState<ExternalCompanyDto>();
  const [loadError, setLoadError] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = useState<Editing>(null);
  const [showMine, setShowMine] = useState(false);
  // Back from Microsoft's sign-in: the result, and the business card open on the row it is about.
  const connectReturn = useConnectReturn();
  const [showBiz, setShowBiz] = useState(false);
  useEffect(() => { if (connectReturn) setShowBiz(true); }, [connectReturn]);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [closingAccount, setClosingAccount] = useState(false);
  const [hoursError, setHoursError] = useState<string>();

  const canEditBusiness = hasPermission(PermissionCode.RbacManage);

  useEffect(() => {
    let active = true;
    setLoadError(undefined);
    Promise.all([getMyProfile(navigate), getMyCompany(navigate)])
      .then(([p, c]) => {
        if (!active) return;
        setProfile(p);
        setCompany(c);
      })
      .catch(e => {
        if (!active || isAuthRedirectError(e)) return;
        logError('Could not load the account', {}, e as Error);
        setLoadError(errorMessage(e, 'Could not load your account.'));
      });
    return () => { active = false; };
  }, [navigate, attempt]);

  // A card with unsaved edits asks before the page is left, by any route.
  const [formDirty, setFormDirty] = useState(false);
  useEffect(() => { if (!editing) setFormDirty(false); }, [editing]);
  const leaveGuard = useLeaveGuard(editing != null && formDirty, `Your changes to ${UNSAVED[editing ?? 'profile']} have not been saved.`);

  // The card being edited comes to the top of the page, its group title just under the green bar.
  const cards = useRef<Partial<Record<string, HTMLDivElement | null>>>({});
  const open = (which: Exclude<Editing, null>, card: string) => {
    setEditing(which);
    window.requestAnimationFrame(() => cards.current[card]?.scrollIntoView({block: 'start', behavior: 'smooth'}));
  };
  const close = useCallback(() => setEditing(null), []);
  /** Another card than these is open: this card's buttons are dimmed. */
  const locked = (own: Editing[] = []) => editing != null && !own.includes(editing);

  /** Saves one day. The page shows the new value at once and puts the old one back with a message if the server refuses it.
   *  Each PUT carries every day, so only the latest one's answer is applied: an earlier one answering late would undo a newer day. */
  const hoursRequest = useRef(0);
  const saveDay = (week: HoursWeek, dayIndex: number, minutes: number) => {
    if (!profile) return;
    const request = ++hoursRequest.current;
    const before = profile.hoursWorked ?? [];
    const after = withDayMinutes(before, week, dayIndex, minutes);
    setProfile({...profile, hoursWorked: after});
    setHoursError(undefined);
    updateMyHoursWorked(navigate, after)
      .then(saved => { if (request === hoursRequest.current) setProfile(saved); })
      .catch(e => {
        if (isAuthRedirectError(e) || request !== hoursRequest.current) return;
        setProfile(p => p && {...p, hoursWorked: before});
        setHoursError(errorMessage(e, 'Could not save those hours.'));
      });
  };

  const photo = hasPermission(PermissionCode.SettingsImageUploadQuality) ? <PhotoQuality/> : null;
  const calendar = hasPermission(PermissionCode.SettingsCalendarIntegrations) ? <CalendarRow/> : null;
  const canConnectEmail = hasPermission(PermissionCode.SettingsEmailIntegrations);

  if (!profile) {
    return (
      <RecordPage name="AccountPage" className="ac">
        <h1 className="ac-title">Account</h1>
        {loadError
          ? <div className="ac-load-error">
              <div>{loadError}</div>
              <OutlinePill text="Try again" onClick={() => setAttempt(a => a + 1)}/>
            </div>
          : <div className="ac-loading"><span className="ac-spinner" role="progressbar" aria-label="Loading your account"/></div>}
      </RecordPage>
    );
  }

  return (
    <RecordPage name="AccountPage" className="ac">
      {leaveGuard}
      <h1 className="ac-title">Account</h1>
      {connectReturn && <NoticeBox text={connectReturn}/>}

      <GroupTitle text="Your details" first/>
      <Card ref={el => { cards.current.profile = el; }} plain className={editing === 'profile' ? 'ac-card--split ac-card--open' : 'ac-card--split'}>
        {editing === 'profile'
          ? <ProfileForm profile={profile} onDirty={setFormDirty} onCancel={close}
                         onSave={async d => {
                           const saved = await updateMyProfile(navigate, {
                             name: d.name.trim(), phoneNumber: blankToNull(d.phone), streetAddress: blankToNull(d.street),
                             city: blankToNull(d.city), state: blankToNull(d.state), postalCode: blankToNull(d.postalCode),
                           });
                           setProfile(saved);
                           close();
                           // users/me carries the name too (the menu's foot).
                           void refreshMe();
                         }}/>
          : <>
              <IdentityRow badge={<InitialsBadge text={initialsOf(profile.name)}/>}
                           name={profile.name.trim() || profile.email || formatPhoneNumber(profile.phoneNumber ?? '')}
                           sub={levelLabel(profile.levelName)}
                           onEdit={() => open('profile', 'profile')} editLocked={locked()}/>
              {showMine && <>
                <DetailRow label="Address" value={joinAddress(profile.streetAddress, profile.city, profile.state, profile.postalCode)}/>
                <DetailRow label="Phone" value={formatPhoneNumber(profile.phoneNumber ?? '')}/>
                <DetailRow label="Email" value={profile.email || 'No email'}/>
              </>}
              <ChevronButton text={showMine ? 'Hide details' : 'Show details'} direction={showMine ? 'up' : 'down'}
                             onClick={() => setShowMine(s => !s)}/>
            </>}
        <HoursSection profile={profile} error={hoursError} onSaveDay={saveDay}/>
      </Card>

      <GroupTitle text="Business details"/>
      <Card ref={el => { cards.current.business = el; }} className={editing === 'business' ? 'ac-card--open' : undefined}>
        {editing === 'business'
          ? <BusinessForm company={company} onDirty={setFormDirty} onCancel={close} onCompany={setCompany}
                          onSave={async d => {
                            const saved = await updateMyCompany(navigate, {
                              name: d.name.trim(), phoneNumber: blankToNull(d.phone), email: blankToNull(d.email),
                              billingStreetAddress1: blankToNull(d.street), billingStreetAddress2: blankToNull(d.street2),
                              billingCity: blankToNull(d.city), billingState: blankToNull(d.state), billingPostalCode: blankToNull(d.postalCode),
                            });
                            setCompany(saved);
                            close();
                          }}
                          canConnectEmail={canConnectEmail}
                          extras={<>{calendar}{photo}</>}/>
          : <>
              <IdentityRow badge={<BusinessBadge logoUrl={company?.logoUrl}/>} name={company?.name?.trim() || 'Your business'}
                           onEdit={canEditBusiness ? () => open('business', 'business') : undefined} editLocked={locked()}/>
              {showBiz && <>
                <DetailRow label="Address" value={joinAddress(
                  [company?.billingStreetAddress1, company?.billingStreetAddress2].filter(s => s?.trim()).join(', '),
                  company?.billingCity, company?.billingState, company?.billingPostalCode)}/>
                <DetailRow label="Phone" value={formatPhoneNumber(company?.phoneNumber ?? '')}/>
                <DetailRow label="Email" value={company?.email ?? ''}/>
                {!canEditBusiness && <><Hairline/><Hint className="ac-pad-hint">Only the account owner can change these.</Hint></>}
                {canConnectEmail && <BusinessEmailRow email={company?.email ?? ''}/>}
                {calendar}
                {photo}
              </>}
              <ChevronButton text={showBiz ? 'Hide details' : 'Show details'} direction={showBiz ? 'up' : 'down'}
                             onClick={() => setShowBiz(s => !s)}/>
            </>}
      </Card>

      {canSeePlan && <>
        <GroupTitle text="Your plan"/>
        <Card plain><PlanSummary locked={locked()}/></Card>
      </>}

      <button className="ac-link-row" onClick={() => navigate('/settings')}>
        <span>App settings</span><Chevron direction="right" size={17} color="var(--ac-green)"/>
      </button>
      <button className="ac-logout" onClick={() => setConfirmLogout(true)}>
        <LogoutGlyph/> Log out
      </button>
      <div className="ac-delete-row">
        <button className="ac-delete" onClick={() => setClosingAccount(true)}>Delete account</button>
      </div>

      {confirmLogout && (
        <Dialog title="Log out?" onClose={() => setConfirmLogout(false)}>
          <div className="ac-dialog-body">You'll need your phone number or email to get back in.</div>
          <div className="ac-dialog-actions">
            <button className="ac-cancel" onClick={() => setConfirmLogout(false)}>Cancel</button>
            <button className="ac-dialog-red" onClick={() => { setConfirmLogout(false); navigate('/logout'); }}>Log out</button>
          </div>
        </Dialog>
      )}
      <CloseAccountDialog open={closingAccount} onClose={() => setClosingAccount(false)}/>
    </RecordPage>
  );
};

/** Tells the page whether a form differs from what it opened with. */
const useDirty = <T, >(draft: T, initial: T, onDirty: (dirty: boolean) => void) => {
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
};

/** A form's save: the button waits while it is out, and a refusal stays on the card with its reason. */
const useSave = <T, >(save: (draft: T) => Promise<void>, fallback: string) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const run = (draft: T) => {
    if (saving) return;
    setSaving(true);
    setError(undefined);
    save(draft)
      .catch(e => {
        if (isAuthRedirectError(e)) return;
        logError(fallback, {}, e as Error);
        setError(errorMessage(e, fallback));
      })
      .finally(() => setSaving(false));
  };
  return {saving, error, run};
};

// ---- Pieces of the cards ----------------------------------------------------------------------

const IdentityRow = ({badge, name, sub, onEdit, editLocked}: {
  badge: React.ReactNode; name: string; sub?: string; onEdit?: () => void; editLocked: boolean;
}) => (
  <div className="ac-identity">
    {badge}
    <div className="ac-grow">
      <div className="ac-name">{name}</div>
      {sub && <div className="ac-sub">{sub}</div>}
    </div>
    {onEdit && <OutlinePill text="Edit" label={`Edit ${name}`} onClick={onEdit} disabled={editLocked}/>}
  </div>
);

/** A filled detail is just shown; a missing one names what is missing (DetailRow). */
const DetailRow = ({label, value}: {label: string; value: string}) => (
  <>
    <Hairline/>
    {value.trim()
      ? <div className="ac-detail">{value}</div>
      : <div className="ac-detail ac-detail--missing"><span>{label}</span><span>Not added</span></div>}
  </>
);

/** The logo when there is one; no logo is just the name, never a placeholder (SQ-653). */
const BusinessBadge = ({logoUrl}: {logoUrl?: string}) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);
  return logoUrl && !failed
    ? <img className="ac-logo-small" src={logoUrl} alt="" onError={() => setFailed(true)}/>
    : null;
};

const LogoutGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H6a1.8 1.8 0 0 0-1.8 1.8v14.4A1.8 1.8 0 0 0 6 21h8"/>
    <path d="M18 15.5L21.5 12L18 8.5M21.5 12H9.5"/>
  </svg>
);

const isCalendar = (i: CompanyIntegrationDto) =>
  i.integrationName?.toLowerCase() === CalendarIntegration.name && i.integrationType?.toLowerCase() === CalendarIntegration.type;

/**
 * ConnectedAccountRow for Google Calendar, with its status (the Account
 * page's layout): Connect signs in with Google for the calendar scope, as
 * the Settings page does; Disconnect removes the company's connection.
 */
const CalendarRow = () => {
  const navigate = useNavigate();
  const [connection, setConnection] = useState<CompanyIntegrationDto | null>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(() => getCompanyIntegrations(navigate)
    .then(list => setConnection(list.find(isCalendar) ?? null))
    .catch(e => {
      if (!isAuthRedirectError(e)) logError('Could not read the calendar connection', {}, e as Error);
      setConnection(null);
    }), [navigate]);
  useEffect(() => { void load(); }, [load]);

  const connect = useGoogleLogin({
    flow: 'auth-code',
    scope: CalendarIntegration.scopes.join(' '),
    onSuccess: async response => {
      try {
        await addCompanyIntegration(navigate, {integrationType: CalendarIntegration.type, integrationName: CalendarIntegration.name, authType: 'oauth2', authCode: response.code});
        await load();
      } catch (e) {
        logError('Connecting Google Calendar failed', {}, e as Error);
        setError('Could not connect Google Calendar.');
      } finally {
        setBusy(false);
      }
    },
    // Backed out of Google's consent: nothing connected, nothing wrong.
    onError: () => setBusy(false),
    onNonOAuthError: () => setBusy(false),
  });

  const disconnect = async () => {
    if (!connection) return;
    setBusy(true);
    setError(undefined);
    try {
      await deleteCompanyIntegration(navigate, connection.id);
      await load();
    } catch (e) {
      logError('Disconnecting Google Calendar failed', {}, e as Error);
      setError('Could not disconnect Google Calendar.');
    } finally {
      setBusy(false);
    }
  };

  const connected = !!connection;
  return (
    <>
      <Hairline/>
      <div className="ac-connect">
        <div className="ac-grow">
          <div className="ac-connect-name">Google Calendar</div>
          <div className="ac-sub">{CalendarIntegration.blurb}</div>
          {error && <div className="ac-error ac-error--small">{error}</div>}
        </div>
        <div className="ac-connect-side">
          {connection !== undefined && <span className="ac-connect-status">{connected ? 'Connected' : 'Not connected'}</span>}
          {busy || connection === undefined
            ? <span className="ac-spinner ac-spinner--small" role="progressbar" aria-label="Working on Google Calendar"/>
            : <button className={connected ? 'ac-connect-pill ac-connect-pill--off' : 'ac-connect-pill'}
                      aria-label={connected ? 'Disconnect Google Calendar' : 'Connect Google Calendar'}
                      onClick={() => {
                        if (connected) { void disconnect(); return; }
                        setBusy(true);
                        setError(undefined);
                        connect();
                      }}>
                {connected ? 'Disconnect' : 'Connect'}
              </button>}
        </div>
      </div>
    </>
  );
};

/** PhotoQualitySection: the person's own setting, 10 steps; saved when the slider is let go. */
const PhotoQuality = () => {
  const {settings, refreshSettings} = useUserSettings();
  const saved = qualityToStep(settings.imageUploadQuality ?? DEFAULT_QUALITY);
  const [step, setStep] = useState(saved);
  const [error, setError] = useState<string>();
  const last = useRef(saved);
  useEffect(() => { setStep(saved); last.current = saved; }, [saved]);

  const commit = () => {
    if (step === last.current) return;
    const before = last.current;
    last.current = step;
    setError(undefined);
    updateUserSettings({imageUploadQuality: step / STEPS})
      .then(() => refreshSettings())
      .catch(e => {
        logError('Could not save the photo quality', {}, e as Error);
        last.current = before;
        setStep(before);
        setError('Could not save the photo quality.');
      });
  };

  const fraction = (step - 1) / (STEPS - 1);
  return (
    <>
      <Hairline/>
      <div className="ac-photo">
        <div className="ac-photo-label">Uploaded photo quality</div>
        <Hint>Lower quality photos upload faster. Higher quality photos look better but take longer to upload.</Hint>
        <div className="ac-slider-row">
          <span className="ac-slider-end">Faster<br/>uploads</span>
          <span className="ac-slider">
            <span className="ac-track">
              <span className="ac-track-fill" style={{width: `${fraction * 100}%`}}/>
              {Array.from({length: STEPS}, (_, i) => (
                <span key={i} className={i + 1 <= step ? 'ac-dot ac-dot--on' : 'ac-dot'} style={{left: `${(i / (STEPS - 1)) * 100}%`}}/>
              ))}
              <span className="ac-thumb" style={{left: `${fraction * 100}%`}}/>
            </span>
            <input type="range" min={1} max={STEPS} step={1} value={step} aria-label="Uploaded photo quality slider"
                   onChange={e => setStep(Number(e.target.value))} onPointerUp={commit} onKeyUp={commit} onBlur={commit}/>
          </span>
          <span className="ac-slider-end ac-slider-end--right">Higher<br/>quality</span>
        </div>
        {error && <div className="ac-error ac-error--small">{error}</div>}
      </div>
    </>
  );
};

// ---- Your details, in place -----------------------------------------------------------------

const ProfileForm = ({profile, onDirty, onCancel, onSave}: {
  profile: MyProfileDto; onDirty: (dirty: boolean) => void; onCancel: () => void; onSave: (d: ProfileDraft) => Promise<void>;
}) => {
  const [initial] = useState<ProfileDraft>(() => ({
    name: profile.name ?? '', phone: phoneDigits(profile.phoneNumber ?? ''), street: profile.streetAddress ?? '',
    city: profile.city ?? '', state: profile.state ?? '', postalCode: profile.postalCode ?? '',
  }));
  const [d, setD] = useState<ProfileDraft>(initial);
  useDirty(d, initial, onDirty);
  const {saving, error, run} = useSave(onSave, 'Could not save your details.');
  const set = (patch: Partial<ProfileDraft>) => setD(x => ({...x, ...patch}));
  return (
    <div className="ac-form ac-form--tint">
      <div className="ac-form-title">Your details</div>
      <div className="ac-fields">
        <Field label="Your name" placeholder="Required" value={d.name} onChange={v => set({name: capitalizeWords(v)})}/>
        <Field label="Your phone" placeholder="(208) 880-1195" inputMode="tel" value={formatPhoneNumber(d.phone)}
               onChange={v => set({phone: phoneDigits(v)})}/>
        <AddressFields prefix="Your" street={d.street} city={d.city} state={d.state} postalCode={d.postalCode} onChange={set}/>
        {profile.email && <>
          <Field label="Your email" value={profile.email} disabled/>
          <Hint>Your email is how you sign in, so it can't be changed here.</Hint>
        </>}
        <Hint>This is you, not the business. None of it is shown to customers. Your permission level is set by whoever runs the account.</Hint>
      </div>
      {error && <div className="ac-error">{error}</div>}
      <EditFoot saveLabel={saving ? 'Saving…' : 'Save changes'} ready={d.name.trim() !== '' && !saving} onCancel={onCancel} onSave={() => run(d)}/>
    </div>
  );
};

/** Street (with the address lookup), then City / State / Zip (2 : 1 : 1); on a wide card one line. A picked suggestion fills all four. */
const AddressFields = ({prefix, street, city, state, postalCode, onChange}: {
  prefix: string; street: string; city: string; state: string; postalCode: string;
  onChange: (patch: {street?: string; city?: string; state?: string; postalCode?: string}) => void;
}) => {
  const {suggestions, active, search, pick, onBlur, onKeyDown} = useStreetSuggestions(a =>
    onChange({street: a.street, city: a.city, state: a.state.toUpperCase().slice(0, 2), postalCode: a.postalCode}));
  return (
    <div className="ac-address">
      <div className="ac-street" onBlur={onBlur} onKeyDown={onKeyDown}>
        <Field label={`${prefix} street address`} value={street} autoComplete="off"
               onChange={v => { onChange({street: v}); search(v); }}/>
        {suggestions.length > 0 && (
          <ul className="ac-suggestions" role="listbox">
            {suggestions.map((s, i) => (
              <li key={s.placeId} role="option" aria-selected={i === active}
                  className={i === active ? 'ac-suggestion ac-suggestion--active' : 'ac-suggestion'}
                  // The press, not the click: the field's blur would close the list under a click.
                  onMouseDown={e => { e.preventDefault(); pick(s); }}>
                {s.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Field label="City" value={city} onChange={v => onChange({city: capitalizeWords(v)})}/>
      <Field label="State" value={state} onChange={v => onChange({state: v.toUpperCase().slice(0, 2)})}/>
      <Field label="Zip" inputMode="numeric" value={postalCode} onChange={v => onChange({postalCode: v.replace(/\D/g, '').slice(0, 5)})}/>
    </div>
  );
};

// ---- Hours worked ------------------------------------------------------------------------------

const HoursSection = ({profile, error, onSaveDay}: {
  profile: MyProfileDto; error?: string; onSaveDay: (week: HoursWeek, dayIndex: number, minutes: number) => void;
}) => {
  const [showAll, setShowAll] = useState(false);
  const [openWeek, setOpenWeek] = useState<string | null>(null);
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [draft, setDraft] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searched, setSearched] = useState<[Date, Date] | null>(null);

  const stored = useMemo(() => weeksFromDto(profile.hoursWorked ?? []), [profile.hoursWorked]);
  const weeks = !showAll ? [] : searched ? weeksBetween(stored, searched[0], searched[1]) : recentWeeks(stored, new Date(), HOURS_EXPANDED);
  const collapse = () => { setShowAll(false); setEditingDay(null); setOpenWeek(null); setSearched(null); };

  return (
    <>
      <div className="ac-hours-head">
        <Hairline green thick/>
        <div className="ac-hours-title">
          <SmallCaps text="Hours worked"/>
          {showAll && <button className="ac-hide" onClick={collapse}>Hide</button>}
        </div>
      </div>
      {error && showAll && <div className="ac-error ac-error--row">{error}</div>}
      {weeks.map(week => {
        const key = dayKey(week.start);
        const isOpen = openWeek === key;
        return (
          <div key={key} className="ac-week-wrap">
            <Hairline/>
            <div className="ac-week">
              <div className="ac-week-label">{weekLabel(week)}</div>
              <div className="ac-week-row">
                <b>{hoursLabel(week.minutes.reduce((a, b) => a + b, 0))}</b>
                <button className="ac-days" onClick={() => setOpenWeek(isOpen ? null : key)} aria-expanded={isOpen}>
                  {isOpen ? 'Hide days' : 'Show days'}<Chevron direction={isOpen ? 'up' : 'down'}/>
                </button>
              </div>
            </div>
            {isOpen && week.minutes.map((value, i) => {
              const dk = `${key}-${i}`;
              const isEditing = editingDay === dk;
              const day = dayLabel(new Date(week.start.getFullYear(), week.start.getMonth(), week.start.getDate() + i));
              return (
                <React.Fragment key={dk}>
                  <Hairline/>
                  <div className="ac-day">
                    <span className="ac-grow">{day}</span>
                    <b className={value === 0 ? 'ac-faint' : undefined}>{hoursLabel(value)}</b>
                    <button className="ac-day-edit" aria-label={`${isEditing ? 'Close' : 'Edit'} ${day}`} onClick={() => {
                      if (isEditing) setEditingDay(null); else { setEditingDay(dk); setDraft(value); }
                    }}>{isEditing ? 'Close' : 'Edit'}</button>
                  </div>
                  {isEditing && <>
                    <Hairline/>
                    <div className="ac-day-editor">
                      <div className="ac-day-draft">{hoursLabel(draft)}</div>
                      <div className="ac-timer" role="group" aria-label={`Hours for ${day}`}>
                        <button onClick={() => setDraft(v => Math.max(0, v - 60))} aria-label="Less, an hour">−</button><span>HR</span>
                        <button onClick={() => setDraft(v => Math.min(1440, v + 60))} aria-label="More, an hour">+</button>
                        <span className="ac-timer-divider"/>
                        <button onClick={() => setDraft(v => Math.max(0, v - 5))} aria-label="Less, five minutes">−</button><span>MIN</span>
                        <button onClick={() => setDraft(v => Math.min(1440, v + 5))} aria-label="More, five minutes">+</button>
                      </div>
                      <div className="ac-day-actions">
                        <button className="ac-day-save" onClick={() => { onSaveDay(week, i, draft); setEditingDay(null); }}>Save</button>
                        <button className="ac-text-button" onClick={() => setEditingDay(null)}>Cancel</button>
                      </div>
                    </div>
                  </>}
                </React.Fragment>
              );
            })}
          </div>
        );
      })}
      {showAll && weeks.length === 0 && <><Hairline/><div className="ac-empty">No hours logged in that range.</div></>}
      <div className={showAll ? 'ac-hours-foot' : 'ac-hours-foot ac-hours-foot--closed'}>
        <ChevronButton text={showAll ? 'Hide hours' : 'Show hours'} direction={showAll ? 'up' : 'down'}
                       onClick={() => showAll ? collapse() : setShowAll(true)}/>
        {showAll && (
          <div className="ac-search">
            <SmallCaps text="Search by date range"/>
            <div className="ac-two">
              <label className="ac-date"><span>From</span><input type="date" value={from} onChange={e => setFrom(e.target.value)}/></label>
              <label className="ac-date"><span>To</span><input type="date" value={to} onChange={e => setTo(e.target.value)}/></label>
            </div>
            <div className="ac-two ac-search-buttons">
              <button className="ac-cancel" onClick={() => { setFrom(''); setTo(''); setSearched(null); }}>Clear</button>
              <button className="ac-primary ac-primary--pair" disabled={!from || !to} onClick={() => {
                const f = parseDay(from);
                const t = parseDay(to);
                if (f && t) { setSearched([f, t]); setOpenWeek(null); setEditingDay(null); }
              }}>Search</button>
            </div>
            {searched && (
              <Hint className="ac-search-hint">
                {isLongRange(searched[0], searched[1])
                  ? 'Showing the weeks with hours in that range above. Clear to go back to the last two weeks.'
                  : 'Showing the weeks in that range above. Clear to go back to the last two weeks.'}
              </Hint>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ---- Business details, in place -------------------------------------------------------------

const BusinessForm = ({company, onDirty, onCancel, onSave, onCompany, canConnectEmail, extras}: {
  company?: ExternalCompanyDto; onDirty: (dirty: boolean) => void; onCancel: () => void; onSave: (d: BusinessDraft) => Promise<void>;
  /** The logo saves on its own, straight away (Android's setLogo): it is a file, not a field of the form. */
  onCompany: (company: ExternalCompanyDto) => void;
  /** settings.email_integrations: the tab under the email field connects the business's mailbox (SQ-743). */
  canConnectEmail: boolean;
  /** The calendar and photo quality, which save on their own, under the form. */
  extras: React.ReactNode;
}) => {
  const [initial] = useState<BusinessDraft>(() => ({
    name: company?.name ?? '', phone: phoneDigits(company?.phoneNumber ?? ''), email: company?.email ?? '',
    street: company?.billingStreetAddress1 ?? '', street2: company?.billingStreetAddress2 ?? '',
    city: company?.billingCity ?? '', state: company?.billingState ?? '', postalCode: company?.billingPostalCode ?? '',
  }));
  const [d, setD] = useState<BusinessDraft>(initial);
  useDirty(d, initial, onDirty);
  const {saving, error, run} = useSave(onSave, 'Could not save the business details.');
  const set = (patch: Partial<BusinessDraft>) => setD(x => ({...x, ...patch}));
  return (
    <>
      <div className="ac-form">
        <div className="ac-form-title">Business details</div>
        <div className="ac-fields">
          <Field label="Business name" placeholder="Required" value={d.name} onChange={v => set({name: v})}/>
          <AddressFields prefix="Business" street={d.street} city={d.city} state={d.state} postalCode={d.postalCode} onChange={set}/>
          {d.street2.trim() !== '' &&
            <Field label="Address line 2" value={d.street2} onChange={v => set({street2: v})}/>}
          <Field label="Business phone" placeholder="(208) 880-1195" inputMode="tel" value={formatPhoneNumber(d.phone)}
                 onChange={v => set({phone: phoneDigits(v)})}/>
          <div>
            <Field label="Business email" placeholder="office@yourcompany.com" inputMode="email" type="email" value={d.email}
                   onChange={v => set({email: v.trim()})}/>
            {canConnectEmail && <SendFromTab email={d.email}/>}
          </div>
          <Hairline/>
          <LogoSection name={d.name} logoUrl={company?.logoUrl} onCompany={onCompany}/>
        </div>
      </div>
      {extras}
      <Hairline/>
      <div className="ac-form ac-form--foot">
        {error && <div className="ac-error">{error}</div>}
        <EditFoot saveLabel={saving ? 'Saving…' : 'Save changes'} ready={d.name.trim() !== '' && !saving} onCancel={onCancel}
                  onSave={() => run(d)}/>
      </div>
    </>
  );
};

const LogoSection = ({name, logoUrl, onCompany}: {name: string; logoUrl?: string; onCompany: (company: ExternalCompanyDto) => void}) => {
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);
  const camera = useRef<HTMLInputElement>(null);
  const photo = useRef<HTMLInputElement>(null);
  const file = useRef<HTMLInputElement>(null);

  const work = (job: () => Promise<ExternalCompanyDto>, fallback: string) => {
    setBusy(true);
    setError(undefined);
    job()
      .then(onCompany)
      .catch(e => {
        if (isAuthRedirectError(e)) return;
        logError(fallback, {}, e as Error);
        setError(errorMessage(e, fallback));
      })
      .finally(() => setBusy(false));
  };
  const picked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) work(async () => uploadLogo(navigate, await logoToPng(f)), 'Could not save the logo.');
  };
  const shown = logoUrl && !failed;

  return (
    <div className="ac-logo-section">
      <div className="ac-photo-label">Company logo</div>
      <Hint>Your logo and name show at the top of every client, appointment, estimate, job and invoice record</Hint>
      <div className="ac-logo-band">
        {shown && <img src={logoUrl} alt="" onError={() => setFailed(true)}/>}
        <div className={shown ? 'ac-logo-name' : 'ac-logo-name ac-logo-name--alone'}>{name.trim() || 'Your business'}</div>
        {busy && <span className="ac-logo-busy"><span className="ac-spinner" role="progressbar" aria-label="Saving your logo"/></span>}
      </div>
      <div className="ac-logo-buttons">
        <OutlinePill text={logoUrl ? 'Change logo' : 'Add logo'} onClick={() => setChoosing(true)} disabled={busy}/>
        {logoUrl && <button className="ac-text-button" disabled={busy}
                            onClick={() => work(() => removeLogo(navigate), 'Could not remove the logo.')}>Remove</button>}
      </div>
      {busy && <Hint className="ac-logo-note">Saving your logo…</Hint>}
      {error && <div className="ac-error ac-error--small">{error}</div>}
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={picked}/>
      <input ref={photo} type="file" accept="image/*" hidden onChange={picked}/>
      <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg" hidden onChange={picked}/>
      {choosing && (
        <Dialog title="Add your logo" onClose={() => setChoosing(false)}>
          <Hint>A photo or screenshot of it works. A proper file (PNG, SVG, JPG…) looks sharpest.</Hint>
          <div className="ac-dialog-choices">
            {([['Take a photo of it', camera], ['Choose a photo or screenshot', photo], ['Choose a file', file]] as const).map(([label, ref]) => (
              <button key={label} className="ac-choice-pill" onClick={() => { setChoosing(false); ref.current?.click(); }}>{label}</button>
            ))}
          </div>
          <button className="ac-text-button ac-dialog-close" onClick={() => setChoosing(false)}>Cancel</button>
        </Dialog>
      )}
    </div>
  );
};

export default AccountPage;
